import React, { useEffect, useId, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { primaryButton } from "./ui";

const EASE_OUT = [0.22, 1, 0.36, 1];

/*
 * Bottom sheet asking before a carrier lets a load go. Keeping it is the
 * filled, focused button at thumb height, so a stray tap does nothing.
 */
export default function ConfirmDecline({ open, onKeep, onConfirm }) {
  const titleId = useId();
  const keepRef = useRef(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    keepRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") return onKeep();
      if (e.key !== "Tab" || !sheetRef.current) return;
      // Keep focus on the sheet; the page behind it is out of reach.
      const buttons = [...sheetRef.current.querySelectorAll("button")];
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const inside = sheetRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [open, onKeep]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            aria-hidden="true"
            onClick={onKeep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.25 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]"
          />
          <motion.div
            key="sheet"
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ y: "100%" }}
            animate={{ y: 0, transition: { duration: 0.4, ease: EASE_OUT } }}
            exit={{
              y: "100%",
              transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
            }}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[480px] rounded-t-[28px] bg-white px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-12px_40px_-12px_rgba(15,23,42,0.35)]"
          >
            <div
              aria-hidden="true"
              className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-slate-200"
            />
            <h2
              id={titleId}
              className="m-0 text-balance text-[28px] font-bold leading-[1.05] text-slate-900"
            >
              Not interested in this load?
            </h2>
            <p className="m-0 mt-2 font-[Roboto] text-[16px] leading-snug text-slate-600">
              We'll let it go and keep looking for your next match.
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={onConfirm}
                className="w-full !rounded-2xl !border-2 !border-solid !border-slate-200 !bg-white hover:!bg-slate-50 !px-4 !py-3.5 !text-[19px] !font-semibold !text-slate-700 !shadow-none !tracking-normal hover:!translate-y-0 active:!scale-[0.98] !transition-[background-color,transform] !duration-200"
              >
                Yes, I'm not interested
              </button>
              <button
                ref={keepRef}
                type="button"
                onClick={onKeep}
                className={primaryButton}
              >
                Keep this load
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
